import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  EyeIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ShieldExclamationIcon,
  ChartBarIcon,
  DocumentTextIcon,
  StarIcon,
  RssIcon,
  DocumentChartBarIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { ToggleStatCard } from '@/components/ui/ToggleStatCard';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import { RichTextDisplay } from '@/components/ui/RichTextEditor';
import threatIntelligenceService, {
  type ThreatAssessment,
  type ThreatFeedItem,
} from '@/services/threat-intelligence.service';
import GenerateReportModal from '@/components/threat-intelligence/GenerateReportModal';
import ReportsList from '@/components/threat-intelligence/ReportsList';
import ThreatFeedItemCard from '@/components/threat-intelligence/ThreatFeedItemCard';

function ThreatAssessmentCard({ assessment }: { assessment: ThreatAssessment }) {
  const threatColors = {
    low: 'bg-success-100 text-success-800 border-success-200 dark:bg-success-900/20 dark:text-success-300 dark:border-success-800',
    medium: 'bg-warning-100 text-warning-800 border-warning-200 dark:bg-warning-900/20 dark:text-warning-300 dark:border-warning-800',
    high: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800',
    critical: 'bg-danger-100 text-danger-800 border-danger-200 dark:bg-danger-900/20 dark:text-danger-300 dark:border-danger-800',
  };

  const confidenceColors = {
    low: 'text-secondary-600 dark:text-secondary-400',
    medium: 'text-warning-600 dark:text-warning-400',
    high: 'text-success-600 dark:text-success-400',
  };

  const statusColors = {
    draft: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
    review: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
    published: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
    archived: 'bg-secondary-100 text-secondary-600 dark:bg-secondary-800 dark:text-secondary-400',
  };

  const categoryIcons = {
    cyber: ShieldExclamationIcon,
    physical: ExclamationTriangleIcon,
    personnel: EyeIcon,
    reputation: DocumentTextIcon,
    financial: ChartBarIcon,
  };

  const CategoryIcon = categoryIcons[assessment.category] || ShieldExclamationIcon;

  const timeAgo = (date: string) => {
    const now = new Date();
    const assessmentDate = new Date(date);
    const diffInHours = Math.floor((now.getTime() - assessmentDate.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) { return 'Just now'; }
    if (diffInHours < 24) { return `${diffInHours}h ago`; }
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  return (
    <Link to={`/security/threats/${assessment.id}`} className="block">
      <Card hover="default" padding="none" className="transition-all duration-200 cursor-pointer">
        <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <CategoryIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg text-secondary-900 dark:text-white truncate">
                {assessment.title}
              </h3>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                {assessment.id} • {assessment.analyst}
              </p>
            </div>
          </div>
        </div>

        <RichTextDisplay
          content={assessment.description}
          className="text-secondary-700 dark:text-secondary-300 text-sm mb-4 line-clamp-2 [&_p]:m-0"
          emptyMessage="No description provided."
        />

        <div className="flex flex-wrap gap-2 mb-4">
          {assessment.threatLevel && (
            <span className={cn('px-3 py-1 text-xs font-medium rounded-full border', threatColors[assessment.threatLevel])}>
              {assessment.threatLevel.toUpperCase()} THREAT
            </span>
          )}
          {assessment.status && (
            <span className={cn('px-3 py-1 text-xs font-medium rounded-full', statusColors[assessment.status])}>
              {assessment.status.toUpperCase()}
            </span>
          )}
          {assessment.category && (
            <span className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300 rounded-full">
              {assessment.category.toUpperCase()}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          {assessment.confidence && (
            <div>
              <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-400 mb-1">
                <StarIcon className="w-4 h-4" />
                <span>Confidence:</span>
              </div>
              <span className={cn('font-medium', confidenceColors[assessment.confidence])}>
                {assessment.confidence.toUpperCase()}
              </span>
            </div>
          )}
          <div>
            <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-400 mb-1">
              <ClockIcon className="w-4 h-4" />
              <span>Updated:</span>
            </div>
            <span className="font-medium text-secondary-900 dark:text-white">
              {timeAgo(assessment.updatedAt)}
            </span>
          </div>
        </div>

        {assessment.targetedAssets && assessment.targetedAssets.length > 0 && (
          <div className="mt-4">
            <div className="text-xs text-secondary-600 dark:text-secondary-400 mb-2">
              Targeted Assets ({assessment.targetedAssets.length}):
            </div>
            <div className="flex flex-wrap gap-1">
              {assessment.targetedAssets.slice(0, 3).map((asset, index) => (
                <span
                  key={index}
                  className="px-2 py-1 text-xs bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300 rounded"
                >
                  {asset}
                </span>
              ))}
              {assessment.targetedAssets.length > 3 && (
                <span className="px-2 py-1 text-xs bg-secondary-100 text-secondary-600 dark:bg-secondary-800 dark:text-secondary-400 rounded">
                  +{assessment.targetedAssets.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}
        </CardContent>
      </Card>
    </Link>
  );
}

interface LocationState {
  activeTab?: 'reported' | 'feed' | 'reports';
}

export default function ThreatIntelligence() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Tab state
  const [view, setView] = useState<'reported' | 'feed' | 'reports'>('reported');

  // Modal state
  const [isGenerateReportModalOpen, setIsGenerateReportModalOpen] = useState(false);

  // Restore active tab from navigation state (when returning from detail pages)
  // Using location.key ensures this runs on each new navigation, not just state changes
  useEffect(() => {
    const state = location.state as LocationState | null;
    if (state?.activeTab) {
      setView(state.activeTab);
    }
  }, [location.key, location.state]);

  // Force refetch feed items when switching to feed tab
  // This is needed because the query's `enabled` condition evaluates before state updates complete
  useEffect(() => {
    if (view === 'feed') {
      void queryClient.invalidateQueries({ queryKey: ['feed-items'] });
    }
  }, [view, queryClient]);

  // Reported Threats filters
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedThreatLevel, setSelectedThreatLevel] = useState<string>(searchParams.get('threatLevel') ?? 'all');
  const [selectedCategory, setSelectedCategory] = useState<string>(searchParams.get('category') ?? 'all');
  const [selectedStatus, setSelectedStatus] = useState<string>(searchParams.get('status') ?? 'all');

  // Feed filters
  const [feedSearchQuery, setFeedSearchQuery] = useState('');
  const [feedThreatLevel, setFeedThreatLevel] = useState<string>('all');
  const [feedCategory, setFeedCategory] = useState<string>('all');
  const [feedLast24hOnly, setFeedLast24hOnly] = useState(false);

  // Fetch threat assessments (manual/reported threats)
  const { data: assessments = [], isLoading: isLoadingAssessments } = useQuery({
    queryKey: ['threat-assessments'],
    queryFn: () => threatIntelligenceService.getThreatAssessments(),
    refetchInterval: 300000,
  });

  // Fetch feed items (RSS imported)
  const { data: feedItemsResponse, isLoading: isLoadingFeedItems } = useQuery({
    queryKey: ['feed-items', feedSearchQuery, feedThreatLevel, feedCategory],
    queryFn: () =>
      threatIntelligenceService.getFeedItems({
        page: 1,
        limit: 50,
        search: feedSearchQuery ?? undefined,
        threatLevel: feedThreatLevel !== 'all' ? feedThreatLevel : undefined,
        category: feedCategory !== 'all' ? feedCategory : undefined,
      }),
    refetchInterval: 300000,
    enabled: view === 'feed',
  });

  const allFeedItems = feedItemsResponse?.data ?? [];
  // "Last 24 Hours" stat card filter — the feed-items API has no date param, so filter client-side
  const feedItems = feedLast24hOnly
    ? allFeedItems.filter((item) => {
        const published = new Date(item.publishedDate ?? item.createdAt).getTime();
        return Date.now() - published <= 24 * 60 * 60 * 1000;
      })
    : allFeedItems;

  // Fetch feed item stats
  const { data: feedStats } = useQuery({
    queryKey: ['feed-item-stats'],
    queryFn: () => threatIntelligenceService.getFeedItemStats(),
    refetchInterval: 300000,
  });

  // Fetch threat stats
  const { data: statsData } = useQuery({
    queryKey: ['threat-stats'],
    queryFn: () => threatIntelligenceService.getThreatStats(),
    refetchInterval: 300000,
  });

  // Fetch reports
  const { data: reports = [], isLoading: isLoadingReports } = useQuery({
    queryKey: ['threat-reports'],
    queryFn: () => threatIntelligenceService.listReports(50),
    refetchInterval: 300000,
  });

  // Mutations
  const deleteReportMutation = useMutation({
    mutationFn: (id: string) => threatIntelligenceService.deleteReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['threat-reports'] });
      toast.success('Report deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete report');
    },
  });

  // Filter reported threats
  const filteredAssessments = assessments.filter((assessment) => {
    const matchesSearch =
      assessment.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      assessment.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      assessment.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesThreatLevel =
      selectedThreatLevel === 'all' || assessment.threatLevel === selectedThreatLevel;
    const matchesCategory =
      selectedCategory === 'all' || assessment.category === selectedCategory;
    const matchesStatus = selectedStatus === 'all' || assessment.status === selectedStatus;

    return matchesSearch && matchesThreatLevel && matchesCategory && matchesStatus;
  });

  // Stats
  const stats = statsData ?? {
    total: assessments.length,
    critical: assessments.filter((a) => a.threatLevel === 'critical').length,
    active: assessments.filter((a) => a.threatLevel === 'high').length,
    monitored: assessments.filter((a) => a.status === 'published').length,
  };

  // Handlers
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleGenerateReport = async (filters: any, format: string, title: string) => {
    try {
      const report = await threatIntelligenceService.generateReport(filters, format as 'html' | 'excel' | 'pdf' | 'csv', title);

      if (report) {
        toast.success('Report generated successfully!');
        setIsGenerateReportModalOpen(false);
        navigate(`/security/threats/reports/${report.id}`);
        queryClient.invalidateQueries({ queryKey: ['threat-reports'] });
      } else {
        toast.error('Failed to generate report');
      }
    } catch (error) {
      toast.error('Failed to generate report');
      throw error;
    }
  };

  const handlePreviewReport = (reportId: string) => {
    navigate(`/security/threats/reports/${reportId}`);
  };

  const handleDownloadReport = async (reportId: string, filename: string) => {
    try {
      const success = await threatIntelligenceService.downloadReport(reportId, filename);
      if (success) {
        toast.success('Report downloaded successfully');
      } else {
        toast.error('Failed to download report');
      }
    } catch (_error) {
      toast.error('Failed to download report');
    }
  };

  const handleDeleteReport = async (reportId: string) => {
    // eslint-disable-next-line no-alert
    if (confirm('Are you sure you want to delete this report? This action cannot be undone.')) {
      await deleteReportMutation.mutateAsync(reportId);
    }
  };

  const handleViewFeedItemDetails = (item: ThreatFeedItem) => {
    navigate(`/security/threats/feeds/${item.id}`, { state: { fromTab: 'feed' } });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
              Threat Intelligence
            </h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Monitor and analyze security threats to your organization
            </p>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={() => setIsGenerateReportModalOpen(true)}>
              <DocumentChartBarIcon className="w-4 h-4 mr-2" />
              Generate Report
            </Button>
            <Button variant="outline" onClick={() => navigate('/security/threats/feeds/manage', { state: { fromTab: view } })}>
              <RssIcon className="w-4 h-4 mr-2" />
              Manage Feeds
            </Button>
            <Button onClick={() => navigate('/security/threats/create')}>
              <PlusIcon className="w-4 h-4 mr-2" />
              New Threat
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Navigation Tabs */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardContent className="p-2">
            <div className="flex space-x-1">
              {[
                { key: 'reported', label: 'Reported Threats' },
                { key: 'feed', label: 'Threats Feed' },
                { key: 'reports', label: 'Reports' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setView(tab.key as typeof view)}
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                    view === tab.key
                      ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/20 dark:text-primary-100'
                      : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:text-secondary-100 dark:hover:bg-secondary-800'
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Stats */}
      <motion.div variants={itemVariants}>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <ToggleStatCard
            label="Reported Threats"
            active={view === 'reported' && selectedThreatLevel === 'all'}
            onToggle={() => {
              setView('reported');
              setSelectedThreatLevel('all');
            }}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-secondary-900 dark:text-white">
                {stats.total ?? 0}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Reported Threats
              </div>
            </CardContent>
          </ToggleStatCard>
          <ToggleStatCard
            label="Critical"
            active={view === 'reported' && selectedThreatLevel === 'critical'}
            activeRingClass="ring-danger-500"
            onToggle={() => {
              setView('reported');
              setSelectedThreatLevel(
                view === 'reported' && selectedThreatLevel === 'critical' ? 'all' : 'critical'
              );
            }}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-danger-600 dark:text-danger-400">
                {stats.critical ?? 0}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Critical
              </div>
            </CardContent>
          </ToggleStatCard>
          <ToggleStatCard
            label="High Priority"
            active={view === 'reported' && selectedThreatLevel === 'high'}
            activeRingClass="ring-orange-500"
            onToggle={() => {
              setView('reported');
              setSelectedThreatLevel(
                view === 'reported' && selectedThreatLevel === 'high' ? 'all' : 'high'
              );
            }}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {stats.active ?? 0}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                High Priority
              </div>
            </CardContent>
          </ToggleStatCard>
          <ToggleStatCard
            label="Feed Items"
            active={view === 'feed' && !feedLast24hOnly}
            onToggle={() => {
              setView('feed');
              setFeedLast24hOnly(false);
            }}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                {feedStats?.total ?? 0}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Feed Items
              </div>
            </CardContent>
          </ToggleStatCard>
          <ToggleStatCard
            label="Last 24 Hours"
            active={view === 'feed' && feedLast24hOnly}
            activeRingClass="ring-success-500"
            onToggle={() => {
              setFeedLast24hOnly(!(view === 'feed' && feedLast24hOnly));
              setView('feed');
            }}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-success-600 dark:text-success-400">
                {(feedStats as { last24h?: number } | null)?.last24h ?? feedStats?.unprocessed ?? 0}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Last 24 Hours
              </div>
            </CardContent>
          </ToggleStatCard>
        </div>
      </motion.div>

      {/* Content based on selected view */}
      {view === 'reported' && (
        <>
          {/* Filters for Reported Threats */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-1">
                    <Input
                      type="text"
                      placeholder="Search reported threats..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 lg:w-auto">
                    <select
                      value={selectedThreatLevel}
                      onChange={(e) => setSelectedThreatLevel(e.target.value)}
                      className="pl-3 pr-9 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="all">All Threat Levels</option>
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="pl-3 pr-9 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="all">All Categories</option>
                      <option value="cyber">Cyber</option>
                      <option value="physical">Physical</option>
                      <option value="personnel">Personnel</option>
                      <option value="reputation">Reputation</option>
                      <option value="financial">Financial</option>
                    </select>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="pl-3 pr-9 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="all">All Status</option>
                      <option value="draft">Draft</option>
                      <option value="review">Under Review</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Reported Threats List */}
          <motion.div variants={itemVariants}>
            <div className="space-y-4">
              {isLoadingAssessments ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-secondary-600 dark:text-secondary-400">
                      Loading reported threats...
                    </p>
                  </CardContent>
                </Card>
              ) : filteredAssessments.length > 0 ? (
                filteredAssessments.map((assessment) => (
                  <motion.div key={assessment.id} variants={itemVariants} layout>
                    <ThreatAssessmentCard assessment={assessment} />
                  </motion.div>
                ))
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <EyeIcon className="w-12 h-12 text-secondary-400 dark:text-secondary-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                      No reported threats found
                    </h3>
                    <p className="text-secondary-600 dark:text-secondary-400">
                      {assessments.length === 0
                        ? 'No threats have been reported yet.'
                        : 'No threats match your current filters. Try adjusting your search criteria.'}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </motion.div>
        </>
      )}

      {view === 'feed' && (
        <>
          {/* Filters for Threats Feed */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-1">
                    <Input
                      type="text"
                      placeholder="Search feed items..."
                      value={feedSearchQuery}
                      onChange={(e) => setFeedSearchQuery(e.target.value)}
                      leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 lg:w-auto">
                    <select
                      value={feedThreatLevel}
                      onChange={(e) => setFeedThreatLevel(e.target.value)}
                      className="pl-3 pr-9 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="all">All Threat Levels</option>
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                    <select
                      value={feedCategory}
                      onChange={(e) => setFeedCategory(e.target.value)}
                      className="pl-3 pr-9 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="all">All Categories</option>
                      <option value="cyber">Cyber</option>
                      <option value="physical">Physical</option>
                      <option value="personnel">Personnel</option>
                      <option value="reputational">Reputational</option>
                      <option value="financial">Financial</option>
                      <option value="operational">Operational</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Feed Items Grid */}
          <motion.div variants={itemVariants}>
            {isLoadingFeedItems ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-secondary-600 dark:text-secondary-400">
                    Loading threat feed...
                  </p>
                </CardContent>
              </Card>
            ) : feedItems.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {feedItems.map((item) => (
                  <motion.div key={item.id} variants={itemVariants} layout className="h-full">
                    <ThreatFeedItemCard
                      item={item}
                      onViewDetails={handleViewFeedItemDetails}
                    />
                  </motion.div>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <RssIcon className="w-12 h-12 text-secondary-400 dark:text-secondary-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                    No feed items found
                  </h3>
                  <p className="text-secondary-600 dark:text-secondary-400 mb-4">
                    {feedSearchQuery || feedThreatLevel !== 'all' || feedCategory !== 'all' || feedLast24hOnly
                      ? 'No items match your current filters. Try adjusting your search criteria.'
                      : 'No threat feed items have been imported yet. Feeds sync automatically every 15 minutes.'}
                  </p>
                  <Button variant="outline" onClick={() => navigate('/security/threats/feeds/manage', { state: { fromTab: view } })}>
                    <RssIcon className="w-4 h-4 mr-2" />
                    Manage Feeds
                  </Button>
                </CardContent>
              </Card>
            )}
          </motion.div>

          {/* Pagination Info */}
          {feedItemsResponse && feedItemsResponse.total > 0 && (
            <motion.div variants={itemVariants}>
              <div className="text-center text-sm text-secondary-600 dark:text-secondary-400">
                Showing {feedItems.length} of {feedItemsResponse.total} feed items
              </div>
            </motion.div>
          )}
        </>
      )}

      {view === 'reports' && (
        <motion.div variants={itemVariants}>
          <ReportsList
            reports={reports}
            isLoading={isLoadingReports}
            onPreviewReport={handlePreviewReport}
            onDownloadReport={handleDownloadReport}
            onDeleteReport={handleDeleteReport}
          />
        </motion.div>
      )}

      {/* Generate Report Modal */}
      <GenerateReportModal
        isOpen={isGenerateReportModalOpen}
        onClose={() => setIsGenerateReportModalOpen(false)}
        onGenerate={handleGenerateReport}
      />
    </motion.div>
  );
}
